import { z } from 'zod';
export const ConversationHandler = z.enum(['bruno', 'queued', 'agent', 'offline_message']);
export const InboxMessageRole = z.enum(['user', 'assistant', 'agent', 'system', 'tool']);
export const ConversationEndActor = z.enum(['visitor', 'agent', 'system']);
/**
 * Minimal info needed to render a row in the cross-website inbox list.
 * Always plain text; never markup. Renderers must use textContent / React
 * text nodes — no innerHTML.
 */
export const ConversationListItem = z.object({
    id: z.string().uuid(),
    websiteId: z.string().uuid(),
    websiteName: z.string().max(200),
    websiteDomain: z.string().max(253),
    handler: ConversationHandler,
    assignedAgentId: z.string().uuid().nullable(),
    assignedAgentName: z.string().max(200).nullable(),
    assignmentLockAt: z.string().max(40).nullable(),
    agentUnread: z.number().int().nonnegative().max(1_000_000),
    lastMessageAt: z.string().max(40).nullable(),
    // Server-built preview, but cap defensively so a hostile DB row can never
    // cause a single inbox.snapshot frame to balloon past the WS payload cap.
    lastMessagePreview: z.string().max(280).nullable(),
    startedAt: z.string().max(40),
    endedAt: z.string().max(40).nullable(),
    /** Who marked the conversation as resolved. NULL if still open. */
    endedBy: ConversationEndActor.nullable(),
    /** Display name of the team member who resolved it (NULL if visitor or
     *  the user record is no longer available). */
    endedByName: z.string().max(200).nullable(),
    /** Optional short note captured at resolution time. Cap matches the
     *  server-side enforcement in routes/ws handlers. Always plain text. */
    endReason: z.string().max(500).nullable(),
    /** Set when this row is an offline-form ticket (handler='offline_message') */
    offlineContact: z
        .object({
        name: z.string().max(100),
        email: z.string().max(254),
        message: z.string().max(2000),
    })
        .nullable(),
});
export const InboxMessage = z.object({
    id: z.string().uuid(),
    role: InboxMessageRole,
    // Bound matches the visitor / agent message caps + headroom for system
    // messages. Anything longer is a stored-DB anomaly we'd rather drop
    // than ship to every connected agent socket.
    content: z.string().max(8000).nullable(),
    senderUserId: z.string().uuid().nullable(),
    senderName: z.string().max(200).nullable(),
    createdAt: z.string().max(40),
});
export const ConversationDetail = z.object({
    conversation: ConversationListItem,
    // Hard cap matches the inbox-detail query (LIMIT 500). Surfaces
    // protocol drift if we ever raise the query without raising this.
    messages: z.array(InboxMessage).max(500),
    visitor: z.object({
        sessionId: z.string().max(64),
        lastPageUrl: z.string().max(4096).nullable(),
    }),
});
export const PostAgentMessageInput = z.object({
    content: z.string().min(1).max(4000),
});
/** Body of POST /v1/inbox/conversations/:id/resolve. The optional reason
 *  is shown to the visitor (if still connected) and persisted as a system
 *  message + on the conversation row. Bound here is mirrored on the WS
 *  side and in the DB column. */
export const ResolveConversationInput = z.object({
    reason: z.string().max(500).optional(),
});
/** Posted by the widget when no agent was online and the visitor filled
 * out the leave-a-message form. The endpoint is unauthenticated and
 * embedKey-bound. */
export const OfflineMessageInput = z.object({
    embedKey: z.string().uuid(),
    sessionId: z.string().uuid(),
    name: z.string().min(1).max(100),
    email: z.string().email().max(254),
    message: z.string().min(1).max(2000),
});
// ---------- Agent WS protocol ----------
/** Agent → server (over /v1/agent-ws) */
export const AgentClientMsg = z.discriminatedUnion('type', [
    z.object({ type: z.literal('hello') }),
    z.object({ type: z.literal('heartbeat') }),
    z.object({
        type: z.literal('agent.message'),
        conversationId: z.string().uuid(),
        content: z.string().min(1).max(4000),
    }),
    z.object({
        type: z.literal('agent.typing'),
        conversationId: z.string().uuid(),
        on: z.boolean(),
    }),
    /**
     * In-window steal of an already-assigned conversation. Kept for
     * backwards compatibility — the legacy auto-assign flow still issues
     * a steal window. New offer-and-claim flow uses `agent.claim_pending`.
     */
    z.object({
        type: z.literal('agent.claim'),
        conversationId: z.string().uuid(),
    }),
    /**
     * First-claim wins for a conversation in `handler='queued'`. The
     * dashboard's "Take" button on the Pending Handoffs row maps to this
     * frame; the Telegram bot's inline keyboard does the same via REST.
     */
    z.object({
        type: z.literal('agent.claim_pending'),
        conversationId: z.string().uuid(),
    }),
    z.object({
        type: z.literal('agent.handback'),
        conversationId: z.string().uuid(),
    }),
    z.object({
        type: z.literal('agent.resolve'),
        conversationId: z.string().uuid(),
        reason: z.string().max(500).optional(),
    }),
    z.object({
        type: z.literal('agent.reopen'),
        conversationId: z.string().uuid(),
    }),
    z.object({
        type: z.literal('presence.set'),
        status: z.enum(['online', 'away', 'offline']),
    }),
]);
//# sourceMappingURL=inbox.js.map